/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import apiClient from '../services/api';
import ErrorMessage from '../components/common/ErrorMessage';

/**
 * Storage key for the persisted Active_Team_Id.
 * Defined by Requirement 17.4.
 */
export const TEAM_STORAGE_KEY = 'reporthub.active_team_id';

/**
 * Hard timeout on the GET /api/v1/users/me/teams call.
 * Defined by Requirement 17.2 (10 seconds).
 */
export const TEAMS_FETCH_TIMEOUT_MS = 10000;

/**
 * Synthetic "All teams" option exposed to Admins (Requirement 17.7).
 * Selecting this option results in `activeTeamId === null`, which means
 * downstream API calls omit the X-Active-Team-Id header.
 */
export const ALL_TEAMS_OPTION = Object.freeze({ id: null, name: 'All teams' });

const TeamContext = createContext(null);

/**
 * Read the persisted Active_Team_Id from localStorage. Returns the literal
 * value `null` for the synthetic "All teams" entry; returns `undefined` when
 * nothing has been persisted (so callers can distinguish the two).
 */
function readPersistedActiveTeamId() {
  try {
    const raw = localStorage.getItem(TEAM_STORAGE_KEY);
    if (raw === null) return undefined;
    if (raw === 'null' || raw === '') return null;
    return raw;
  } catch {
    return undefined;
  }
}

function writePersistedActiveTeamId(id) {
  try {
    if (id === null) {
      localStorage.setItem(TEAM_STORAGE_KEY, 'null');
    } else if (id === undefined) {
      localStorage.removeItem(TEAM_STORAGE_KEY);
    } else {
      localStorage.setItem(TEAM_STORAGE_KEY, String(id));
    }
  } catch {
    // localStorage unavailable; selection is in-memory only
  }
}

/**
 * Sort a list of teams alphabetically by `name`, case-insensitively, in a
 * locale-aware manner (Requirement 17.4).
 */
export function sortTeamsAlphabetically(teams) {
  return [...teams].sort((a, b) =>
    String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' })
  );
}

/**
 * Normalise a team record from the API into the shape consumed by the UI.
 * Accepts either lowercase (`team_id`, `name`) or uppercase (`TEAM_ID`,
 * `NAME`) attribute names so the context is robust to backend-key casing.
 */
function normaliseTeam(team) {
  if (!team) return null;
  const id = team.team_id ?? team.TEAM_ID ?? team.id ?? team.ID ?? null;
  const name = team.name ?? team.NAME ?? '';
  if (id === null) return null;
  return { id, name };
}

/**
 * Resolve the initial Active_Team_Id given the fetched authorised teams,
 * any persisted value, and admin status.
 *
 * Rules (Requirements 17.3, 17.4, 17.7):
 *   - Single team and not admin → that team is auto-selected.
 *   - Multi-team and not admin → persisted value if still in the authorised
 *     set, otherwise the first team in case-insensitive alphabetical order.
 *   - Admin → persisted value if it identifies one of the real teams,
 *     otherwise the synthetic "All teams" option (id = null).
 *   - Zero teams and not admin → null (no Active_Team_Id; callers render the
 *     empty-state per Requirement 5.6).
 */
export function resolveActiveTeamId({ realTeams, persisted, isAdmin }) {
  if (isAdmin) {
    if (persisted !== undefined && persisted !== null && realTeams.some((t) => t.id === persisted)) {
      return persisted;
    }
    return null; // synthetic "All teams"
  }
  if (realTeams.length === 0) return null;
  if (realTeams.length === 1) return realTeams[0].id;
  if (persisted !== undefined && persisted !== null && realTeams.some((t) => t.id === persisted)) {
    return persisted;
  }
  return sortTeamsAlphabetically(realTeams)[0].id;
}

/**
 * Race a promise against a hard timeout; the returned promise rejects with a
 * dedicated `Error` whose `name === 'TeamsFetchTimeout'` after `timeoutMs`.
 * The `controller` is aborted when the timeout fires so the underlying axios
 * request is also cancelled.
 */
function withTimeout(promise, timeoutMs, controller) {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      const err = new Error(`Request timed out after ${timeoutMs} ms`);
      err.name = 'TeamsFetchTimeout';
      try { controller.abort(); } catch { /* no-op */ }
      reject(err);
    }, timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => clearTimeout(timeoutId));
}

/**
 * TeamProvider.
 *
 * Fetches `/api/v1/users/me/teams` once on mount and exposes the resolved
 * authorised teams, the Active_Team_Id, an admin flag, loading/error state,
 * and a `retry` callback. Persists `activeTeamId` to localStorage under
 * `reporthub.active_team_id`. Reconciles persisted values against the
 * fetched authorised set and drops a stale persisted value if it is no
 * longer authorised.
 *
 * Renders an error banner with retry on failure or 10-second timeout, and an
 * empty-state banner for non-admins with no team membership. While loading
 * or in error, downstream consumers must not render tenant data; they can
 * gate on `loading`, `error`, and `authorisedTeams.length` from the hook.
 */
export function TeamProvider({ children }) {
  const [authorisedTeams, setAuthorisedTeams] = useState([]);
  const [activeTeamId, setActiveTeamIdState] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Guard against stale fetches updating state after unmount or after a more
  // recent fetch has been kicked off (e.g. by retry()).
  const fetchSeqRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => () => { isMountedRef.current = false; }, []);

  const fetchTeams = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);

    const controller = new AbortController();

    try {
      const response = await withTimeout(
        apiClient.get('/users/me/teams', { signal: controller.signal }),
        TEAMS_FETCH_TIMEOUT_MS,
        controller
      );

      if (!isMountedRef.current || seq !== fetchSeqRef.current) return;

      const payload = response?.data?.data ?? response?.data ?? {};
      const rawTeams = Array.isArray(payload.teams) ? payload.teams : [];
      const adminFlag = Boolean(payload.is_admin ?? payload.IS_ADMIN);

      const realTeams = rawTeams.map(normaliseTeam).filter(Boolean);
      const sortedReal = sortTeamsAlphabetically(realTeams);
      const exposedTeams = adminFlag ? [...sortedReal, ALL_TEAMS_OPTION] : sortedReal;

      const persisted = readPersistedActiveTeamId();
      const nextActive = resolveActiveTeamId({
        realTeams: sortedReal,
        persisted,
        isAdmin: adminFlag,
      });

      setAuthorisedTeams(exposedTeams);
      setIsAdmin(adminFlag);
      setActiveTeamIdState(nextActive);
      // Reconcile localStorage with the resolved value so a stale persisted
      // entry is dropped (Requirement 17.4).
      writePersistedActiveTeamId(nextActive);
      setLoading(false);
    } catch (err) {
      if (!isMountedRef.current || seq !== fetchSeqRef.current) return;
      const message = err?.name === 'TeamsFetchTimeout'
        ? 'Loading your teams timed out. Please try again.'
        : err?.message || 'Failed to load your teams.';
      setAuthorisedTeams([]);
      setIsAdmin(false);
      setActiveTeamIdState(null);
      setError(message);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  const setActiveTeamId = useCallback((id) => {
    setActiveTeamIdState(id);
    writePersistedActiveTeamId(id);
  }, []);

  const retry = useCallback(() => {
    fetchTeams();
  }, [fetchTeams]);

  const value = {
    authorisedTeams,
    activeTeamId,
    setActiveTeamId,
    isAdmin,
    loading,
    error,
    retry,
  };

  // Banner-rendering rules:
  //   - On error or timeout: render <ErrorMessage> with retry (R17.2).
  //   - On non-admin with empty Authorised_Team_Set: render the empty-state
  //     banner so children that fail to gate on context state still produce
  //     a clear UX (R5.6).
  // Children are still rendered so that nested providers/routes can mount
  // and consumer pages can read context state to suppress tenant data
  // themselves.
  const showNoTeamsBanner = !loading && !error && !isAdmin && authorisedTeams.length === 0;

  return (
    <TeamContext.Provider value={value}>
      {error && (
        <ErrorMessage message={error} onRetry={retry} />
      )}
      {showNoTeamsBanner && (
        <div className="team-empty-state" role="status" data-testid="team-empty-state">
          <p>You are not a member of any team. Contact your administrator to be added to a team before you can view reports.</p>
        </div>
      )}
      {children}
    </TeamContext.Provider>
  );
}

TeamProvider.propTypes = {
  children: PropTypes.node,
};

/**
 * useTeam Hook
 *
 * Custom hook to access the TeamContext. Throws when called outside a
 * TeamProvider, mirroring useAuth's pattern in this codebase.
 */
export function useTeam() {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
}

export default TeamContext;
